/**
 * @file render-engine.ts
 * @description The core compositing and rendering engine for the drawing canvas.
 */

import { g, layers, LayerClass, Tool } from '@hcie/core';
import { renderVectorShapesToCanvas, EventBus } from '@hcie/shared';
import { 
    drawMarchingAnts, 
    drawCropUI, 
    drawBrushPreviewCircle,
    drawFloatingWarning
} from './ui/render-helpers';

/**
 * @function renderLayers
 * @description Performs a full composite of all visible layers onto the display canvas.
 * @param {HTMLCanvasElement} [liveCanvas] - Optional buffer for active drawing preview.
 * @returns {void}
 */
export function renderLayers(liveCanvas?: HTMLCanvasElement): void {
    // [CRITICAL_LOGIC_START]
    const drawingCanvas = document.getElementById("drawingCanvas") as HTMLCanvasElement;
    if (!drawingCanvas) return;
    
    const displayCtx = drawingCanvas.getContext("2d");
    if (!displayCtx) return;

    // STEP 1: Preparation
    // Use the temporary canvas if a live stroke is being drawn.
    if (!liveCanvas && g.drawing) {
        liveCanvas = (window as any).tempCanvas || document.getElementById("tempCanvas") as HTMLCanvasElement;
    }

    displayCtx.clearRect(0, 0, g.image_width, g.image_height);

    // STEP 2: Compositing Layers (Bottom-to-Top)
    for (let i = 0; i < layers.length; i++) {
        const layer: LayerClass = layers[i];
        if (!layer.visible) continue;

        displayCtx.save();
        displayCtx.globalAlpha = layer.opacity;
        displayCtx.globalCompositeOperation = layer.blendMode;

        // Draw raster pixels
        displayCtx.drawImage(layer.canvas as HTMLCanvasElement, 0, 0);

        // Draw permanent shapes on this layer
        if (layer.shapes && layer.shapes.length > 0) {
            renderLayerShapes(displayCtx, layer, i);
        }

        // STEP 3: Raster Tool Live Rendering
        // Only raster brushes stay at their layer depth for natural occlusion during drawing
        if (i === g.activeLayerIndex) {
            renderRasterToolPreview(displayCtx, liveCanvas);
        }

        displayCtx.restore();
    }

    // STEP 4: Global Overlays
    // Marching ants for selection, crop area boundary, and brush preview circle.
    drawGlobalOverlays(displayCtx);

    // Synchronize originalCanvas for backward compatibility with filter tools.
    syncOriginalCanvas(displayCtx.canvas);
    // [CRITICAL_LOGIC_END]
}

/**
 * @function renderLayerShapes
 * @description Renders the vector shapes belonging to a specific layer.
 * @private
 */
function renderLayerShapes(ctx: CanvasRenderingContext2D, layer: LayerClass, index: number): void {
    const vectorToolManager = (window as any).vectorToolManager;
    const editingTarget = (window as any).vectorEditingTarget;
    const layerShapes = layer.shapes || [];
    
    const isTargetingThisLayer: boolean = 
        vectorToolManager?.isEditing &&
        editingTarget?.layerIndex === index &&
        typeof editingTarget.shapeIndex === 'number' &&
        editingTarget.shapeIndex >= 0;

    if (isTargetingThisLayer) {
        // Exclude the shape being edited from the static background render
        const staticShapes = layerShapes.filter((_, idx) => idx !== editingTarget.shapeIndex);
        renderVectorShapesToCanvas(ctx, staticShapes);
    } else {
        renderVectorShapesToCanvas(ctx, layerShapes);
    }
}

/**
 * @function renderRasterToolPreview
 * @description Draws in-progress raster strokes (Pen/Brush/Eraser) on top of the active layer.
 * @private
 */
function renderRasterToolPreview(ctx: CanvasRenderingContext2D, liveCanvas?: HTMLCanvasElement): void {
    if (liveCanvas) {
        const isEraser: boolean = g.current_tool.id === Tool.Eraser.id;
        
        if (g.isSelectionActive && g.selectionCanvas) {
            applySelectionMaskedStroke(ctx, liveCanvas, isEraser);
        } else {
            if (isEraser) ctx.globalCompositeOperation = "destination-out";
            ctx.drawImage(liveCanvas, 0, 0);
        }
    }
}

/**
 * @function renderVectorToolPreview
 * @description Draws in-progress vector shapes globally on top of all layers for better feedback.
 * @private
 */
function renderVectorToolPreview(ctx: CanvasRenderingContext2D): void {
    const vectorToolManager = (window as any).vectorToolManager;
    const isVtmActive: boolean = vectorToolManager && (vectorToolManager.isDrawing || vectorToolManager.isEditing) && vectorToolManager.currentShape;
    
    if (isVtmActive) {
        renderVectorShapesToCanvas(ctx, [vectorToolManager.currentShape]);
    }
}

/**
 * @function applySelectionMaskedStroke
 * @description Composites a live stroke through a selection mask buffer.
 * @private
 */
function applySelectionMaskedStroke(ctx: CanvasRenderingContext2D, strokeSource: HTMLCanvasElement, isEraser: boolean): void {
    if (!(window as any)._maskedStrokeBuffer) {
        (window as any)._maskedStrokeBuffer = g.createCanvas(g.image_width, g.image_height);
    }
    
    const bufferCanvas: HTMLCanvasElement = (window as any)._maskedStrokeBuffer;
    if (bufferCanvas.width !== g.image_width || bufferCanvas.height !== g.image_height) {
        bufferCanvas.width = g.image_width;
        bufferCanvas.height = g.image_height;
    }
    
    const bufferCtx = bufferCanvas.getContext("2d");
    if (!bufferCtx) return;

    bufferCtx.clearRect(0, 0, g.image_width, g.image_height);
    bufferCtx.globalCompositeOperation = "source-over";
    bufferCtx.globalAlpha = 1.0;
    bufferCtx.drawImage(strokeSource, 0, 0);
    bufferCtx.globalCompositeOperation = "destination-in";
    bufferCtx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
    
    if (isEraser) ctx.globalCompositeOperation = "destination-out";
    ctx.drawImage(bufferCanvas, 0, 0);
}

/**
 * @function drawGlobalOverlays
 * @description Draws non-destructive UI elements on top of all layers.
 * @private
 */
function drawGlobalOverlays(ctx: CanvasRenderingContext2D): void {
    // 1. Moving selection content
    if (g.floatingContent?.canvas) {
        const fc = g.floatingContent;
        const centerX = fc.x + fc.canvas.width / 2;
        const centerY = fc.y + fc.canvas.height / 2;
        
        ctx.save();
        ctx.imageSmoothingEnabled = g.imageSmoothingEnabled;
        ctx.translate(centerX, centerY);
        if (fc.angle) {
            ctx.rotate(fc.angle);
        }
        if (fc.scaleX !== undefined && fc.scaleY !== undefined) {
            ctx.scale(fc.scaleX, fc.scaleY);
        }
        ctx.drawImage(fc.canvas as HTMLCanvasElement, -fc.canvas.width / 2, -fc.canvas.height / 2);
        ctx.restore();
    }

    // 2. Transformed Selection Handles (Ghost Selection)
    if ((g as any).settings?.selection?.ghostMode && g.isSelectionActive && (g as any)._selectionEditBounds) {
        drawGhostSelectionHandles(ctx);
    }

    // 3. Vector tool life preview
    renderVectorToolPreview(ctx);

    // 4. System helpers
    drawMarchingAnts(ctx);
    drawCropUI(ctx);
    drawBrushPreviewCircle(ctx);
    
    // 5. Contextual Feedback
    drawFloatingWarning(ctx);
}

/**
 * @function drawGhostSelectionHandles
 * @description Helper to draw selection transformation handles.
 * @private
 */
function drawGhostSelectionHandles(ctx: CanvasRenderingContext2D): void {
    const bounds = (g as any)._selectionEditBounds;
    const x1 = bounds.x1, y1 = bounds.y1, x2 = bounds.x2, y2 = bounds.y2;
    const handleVisualSize: number = 10 / (g.zoom || 1);
    
    const controlPoints = [
        { x: x1, y: y1 }, { x: x2, y: y1 }, { x: x1, y: y2 }, { x: x2, y: y2 },
        { x: (x1 + x2) / 2, y: y1 }, { x: (x1 + x2) / 2, y: y2 },
        { x: x1, y: (y1 + y2) / 2 }, { x: x2, y: (y1 + y2) / 2 }
    ];

    ctx.save();
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#00bcd4";
    ctx.lineWidth = Math.max(1, 1 / (g.zoom || 1));
    for (const point of controlPoints) {
        ctx.beginPath();
        ctx.rect(point.x - handleVisualSize / 2, point.y - handleVisualSize / 2, handleVisualSize, handleVisualSize);
        ctx.fill();
        ctx.stroke();
    }
    ctx.restore();
}

/**
 * @function syncOriginalCanvas
 * @description Synchronizes the legacy 'originalCanvas' element used by some older filter tools.
 * @private
 */
function syncOriginalCanvas(displayCanvas: HTMLCanvasElement): void {
    const originalCanvas = document.getElementById('originalCanvas') as HTMLCanvasElement | null;
    if (originalCanvas) {
        const origCtx = originalCanvas.getContext("2d");
        if (origCtx) {
            origCtx.clearRect(0, 0, g.image_width, g.image_height);
            origCtx.drawImage(displayCanvas, 0, 0);
        }
    }
}

/**
 * @function renderTextLayer
 * @description Renders a text-only layer specialized for typography.
 * @param {LayerClass} layer - The layer containing text data.
 */
export function renderTextLayer(layer: LayerClass): void {
    if (!layer || layer.type !== 'text' || layer.isBeingEdited) return;

    const ctx: CanvasRenderingContext2D = (layer.ctx as any);
    const data = (layer.textData as any);
    if (!ctx || !data) return;

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    ctx.save();
    const style = data.italic ? 'italic' : 'normal';
    const weight = data.bold ? 'bold' : 'normal';
    ctx.font = `${style} ${weight} ${data.size}px ${data.font}, "Roboto", sans-serif`;
    ctx.fillStyle = data.color;
    ctx.textBaseline = 'top';

    const lines: string[] = (data.text || "").split('\n');
    const lineHeight: number = data.size * 1.2;

    lines.forEach((line, index) => {
        ctx.fillText(line, data.x, data.y + (index * lineHeight));
    });
    ctx.restore();
}
